import React, { useEffect, useMemo, useState } from 'react';
import {
  COMPANY_FEATURES,
  DEFAULT_COMPANY_FEATURE_ACCESS,
  fetchFeatureAccessCompanies,
  normalizeCompanyFeatureAccess,
  saveCompanyFeatureAccess,
} from '../../utils/companyFeatureAccess';

const ADMIN_HIDDEN_FEATURES = new Set(['applicant_sync', 'scheduling']);
const ADMIN_VISIBLE_FEATURES = Object.entries(COMPANY_FEATURES).filter(([key]) => !ADMIN_HIDDEN_FEATURES.has(key));

const AdminFeatureAccessPage = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [features, setFeatures] = useState(DEFAULT_COMPANY_FEATURE_ACCESS);
  const [search, setSearch] = useState('');
  const [featureFilter, setFeatureFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const selectedCompany = useMemo(
    () => companies.find((company) => String(company.id) === String(selectedCompanyId)) || null,
    [companies, selectedCompanyId],
  );

  const loadCompanies = async () => {
    setLoading(true);
    setMessage('');
    try {
      const rows = await fetchFeatureAccessCompanies({
        search,
        feature: featureFilter,
        enabled: enabledFilter,
      });
      setCompanies(rows);
      const nextSelected = rows.find((row) => String(row.id) === String(selectedCompanyId)) || rows[0] || null;
      setSelectedCompanyId(nextSelected?.id || null);
      setFeatures(normalizeCompanyFeatureAccess(nextSelected?.features));
    } catch (error) {
      setMessage(error?.message || 'Failed to load companies.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectCompany = (company) => {
    setSelectedCompanyId(company.id);
    setFeatures(normalizeCompanyFeatureAccess(company.features));
    setMessage('');
  };

  const toggleFeature = (key) => {
    setFeatures((prev) => ({ ...normalizeCompanyFeatureAccess(prev), [key]: !normalizeCompanyFeatureAccess(prev)[key] }));
  };

  const save = async () => {
    if (!selectedCompanyId) return;
    setSaving(true);
    setMessage('');
    try {
      const saved = await saveCompanyFeatureAccess(selectedCompanyId, features);
      setFeatures(saved);
      setCompanies((prev) => prev.map((company) => (
        String(company.id) === String(selectedCompanyId)
          ? { ...company, features: saved, has_config: true }
          : company
      )));
      setMessage('Feature access saved successfully.');
    } catch (error) {
      setMessage(error?.message || 'Failed to save feature access.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Company Feature Access</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Choose which application modules each company can use. Companies without a saved config keep full access.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-3 flex gap-2">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search company"
              className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
            <button
              type="button"
              onClick={loadCompanies}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              disabled={loading}
            >
              Filter
            </button>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-2">
            <select
              value={featureFilter}
              onChange={(event) => setFeatureFilter(event.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            >
              <option value="">All features</option>
              {ADMIN_VISIBLE_FEATURES.map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <select
              value={enabledFilter}
              onChange={(event) => setEnabledFilter(event.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            >
              <option value="">Any status</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>

          <div className="max-h-[calc(100vh-280px)] space-y-2 overflow-y-auto pr-1">
            {companies.map((company) => (
              <button
                key={company.id}
                type="button"
                onClick={() => selectCompany(company)}
                className={`w-full rounded-lg border px-3 py-3 text-left transition ${
                  String(selectedCompanyId) === String(company.id)
                    ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30'
                    : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700'
                }`}
              >
                <div className="font-semibold text-gray-900 dark:text-white">{company.company_name || `Company ${company.id}`}</div>
                <div className="mt-1 text-xs text-gray-500">
                  ID {company.id} · {company.has_config ? 'Configured' : 'Full access default'}
                </div>
              </button>
            ))}
            {!loading && companies.length === 0 && (
              <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
                No companies found.
              </div>
            )}
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {selectedCompany?.company_name || 'Select a company'}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Toggle access for each process in the application.
              </p>
            </div>
            <button
              type="button"
              onClick={save}
              disabled={!selectedCompanyId || saving}
              className="rounded-lg bg-violet-600 px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save Access'}
            </button>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {ADMIN_VISIBLE_FEATURES.map(([key, label]) => {
              const enabled = normalizeCompanyFeatureAccess(features)[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleFeature(key)}
                  className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left transition ${
                    enabled
                      ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/20'
                      : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900'
                  }`}
                >
                  <span>
                    <span className="block font-semibold text-gray-900 dark:text-white">{label}</span>
                    <span className="text-xs text-gray-500">{key}</span>
                  </span>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${
                    enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {enabled ? 'ON' : 'OFF'}
                  </span>
                </button>
              );
            })}
          </div>

          {message && (
            <div className="mt-5 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-800">
              {message}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminFeatureAccessPage;
